import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface ColumnDef { id: string; title: string; }
interface RowDef { id: string; title: string; answer_id?: string | null; }

@Component({
  selector: 'app-grid-question-table',
  templateUrl: './grid-question-table.component.html',
  styleUrls: ['./grid-question-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GridQuestionTableComponent {
  private _columns: ColumnDef[] = [];
  private _rows: RowDef[] = [];

  @Output() columnsChange = new EventEmitter<ColumnDef[]>();
  @Output() rowsChange = new EventEmitter<RowDef[]>();

  /** Flag per mostrare la fase di risposta */
  @Input() isResponse = false;
  @Input() editable = true;

  /** Dati per la view di configurazione */
  displayColumns: ColumnDef[] = [];
  displayRows: RowDef[] = [];

  /** Definizione colonne per mat-table in risposta */
  displayedColumns: string[] = [];

  @Input()
  set columns(cols: ColumnDef[] | null) {
    const source = cols || [];
    // copia e forziamo id come string
    this._columns = source.map(c => ({ id: String(c.id), title: c.title }));
    this.ensureMinimumColumns();
    this.displayColumns = [...this._columns];
    this.updateTableColumns();
  }
  get columns(): ColumnDef[] {
    return [...this._columns];
  }

  @Input()
  set rows(rws: RowDef[] | null) {
    const source = rws || [];
    this._rows = source.map(r => ({ id: String(r.id), title: r.title, answer_id: r.answer_id ? String(r.answer_id) : null }));
    this.ensureMinimumRows();
    this.displayRows = [...this._rows];
    this.updateTableColumns(); // Rows change might not affect columns structure but good to be safe if we change logic later
  }
  get rows(): RowDef[] {
    return [...this._rows];
  }

  constructor(private cd: ChangeDetectorRef) { }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  }

  private ensureMinimumColumns() {
    while (this._columns.length < 2) {
      this._columns.push({
        id: this.generateId('col'),
        title: `Colonna ${this._columns.length + 1}`
      });
    }
  }

  private ensureMinimumRows() {
    while (this._rows.length < 2) {
      this._rows.push({
        id: this.generateId('row'),
        title: `Riga ${this._rows.length + 1}`,
        answer_id: null
      });
    }
  }

  /** Ricombina l'array displayedColumns per mat-table */
  private updateTableColumns(): void {
    if (this.displayColumns) {
      this.displayedColumns = ['rowID', ...this.displayColumns.map(c => c.id)];
      this.cd.markForCheck();
    }
  }

  public emitChanges(): void {
    // Emesso sempre string IDs
    this.columnsChange.emit(this.displayColumns.map(c => ({ ...c })));
    this.rowsChange.emit(this.displayRows.map(r => ({ ...r })));
    this._columns = [...this.displayColumns];
    this._rows = [...this.displayRows];
    this.updateTableColumns();
  }

  addColumn(): void {
    const idx = this.displayColumns.length + 1;
    this.displayColumns = [
      ...this.displayColumns,
      { id: this.generateId('col'), title: `Colonna ${idx}` }
    ];
    this.emitChanges();
  }

  removeColumn(index: number): void {
    this.displayColumns = this.displayColumns.filter((_, i) => i !== index);
    this.emitChanges();
  }

  addRow(): void {
    const idx = this.displayRows.length + 1;
    this.displayRows = [
      ...this.displayRows,
      { id: this.generateId('row'), title: `Riga ${idx}`, answer_id: null }
    ];
    this.emitChanges();
  }

  removeRow(index: number): void {
    this.displayRows = this.displayRows.filter((_, i) => i !== index);
    this.emitChanges();
  }

  /** Callback quando cambia la risposta di una riga */
  onAnswerChange(changedRow: RowDef): void {
    const idx = this.displayRows.findIndex(r => r.id === changedRow.id);
    if (idx > -1) {
      // forziamo string value
      this.displayRows[idx] = { ...changedRow, answer_id: String(changedRow.answer_id) };
      this.rowsChange.emit(this.displayRows.map(r => ({ ...r })));
    }
  }

  dropRow(event: CdkDragDrop<RowDef[]>) {
    moveItemInArray(this.displayRows, event.previousIndex, event.currentIndex);
    this.emitChanges();
  }

  dropColumn(event: CdkDragDrop<ColumnDef[]>) {
    moveItemInArray(this.displayColumns, event.previousIndex, event.currentIndex);
    this.emitChanges();
  }
}
